import { action, computed, makeAutoObservable } from 'mobx';
import * as t from 'io-ts';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { LiveNotificationsState } from './LiveNotificationsState';
import { LiveNotificationsModelType } from 'src/api/config/live_notifications/get_live_notifications';
import { createGuard } from 'src_common/common/createGuard';

interface DataType {
    id: number;
    direction: 'up' | 'down';
}

const NotificationIdsIO = t.interface({
    idsList: t.array(t.number),
});

const isNotificationIds = createGuard(NotificationIdsIO);

export class LiveNotificationsItemState {
    public constructor(
        private readonly id: number,
        private liveNotificationItem: () => LiveNotificationsModelType | null,
        private liveNotificationsState: LiveNotificationsState,
        private trpcClient: TrpcClient
    ) {
        makeAutoObservable(this);
    }

    @computed public get liveNotificationItemData(): LiveNotificationsModelType | null {
        return this.liveNotificationItem();
    }

    @computed public get liveNotificationId(): number {
        return this.id;
    }

    @computed public get isEdited(): boolean {
        return this.liveNotificationId === this.liveNotificationsState.editedLiveNotification?.id;
    }

    @computed public get allowedIdsListFilled(): boolean {
        return isNotificationIds(this.liveNotificationItemData?.users_ids)
            ? this.liveNotificationItemData.users_ids.idsList.length > 0
            : false;
    }

    @action public setShowForAll = (value: boolean): void => {
        this.liveNotificationsState.showForAll = value;
    };

    @action public handleEdit = async (): Promise<void> => {
        const liveNotificationItem = this.liveNotificationItem();
        if (liveNotificationItem === null) {
            console.error('handleEdit - ignore');
            return;
        }

        this.liveNotificationsState.resetForm();
        await this.liveNotificationsState.setEditedLiveNotification(liveNotificationItem);
    };

    public moveItemUp = async (): Promise<void> => {
        const data: DataType = {
            id: this.liveNotificationId,
            direction: 'up',
        };

        try {
            await this.trpcClient.client.liveNotificationsRouter.changeLiveNotificationsOrder.mutate({ data });
            await this.liveNotificationsState.liveNotificationsResource.refresh();
        } catch (error) {
            console.error(error);
        }
    };

    public moveItemDown = async (): Promise<void> => {
        const data: DataType = {
            id: this.liveNotificationId,
            direction: 'down',
        };

        try {
            await this.trpcClient.client.liveNotificationsRouter.changeLiveNotificationsOrder.mutate({ data });
            await this.liveNotificationsState.liveNotificationsResource.refresh();
        } catch (error) {
            console.error(error);
        }
    };

    @action public setActive = async (): Promise<void> => {
        const liveNotificationItem = this.liveNotificationItem();
        if (liveNotificationItem === null) {
            console.error('setActive - ignore');
            return;
        }

        const updatedItemData = {
            ...liveNotificationItem,
            is_active: !liveNotificationItem.is_active,
        };

        try {
            await this.trpcClient.client.liveNotificationsRouter.editLiveNotification.mutate({
                data: updatedItemData,
                id: liveNotificationItem.id,
            });
            await this.liveNotificationsState.liveNotificationsResource.refresh();
        } catch (error) {
            console.error(error);
        }
    };

    @action public setActiveForAllUsersFlag = async (): Promise<void> => {
        const liveNotificationItem = this.liveNotificationItem();
        if (liveNotificationItem === null) {
            console.error('setActiveForAllUsersFlag - ignore');
            return;
        }

        const updatedItemData = {
            ...liveNotificationItem,
            is_show_for_all_users: !liveNotificationItem.is_show_for_all_users,
        };

        try {
            await this.trpcClient.client.liveNotificationsRouter.editLiveNotification.mutate({
                data: updatedItemData,
                id: liveNotificationItem.id,
            });
            await this.liveNotificationsState.liveNotificationsResource.refresh();
        } catch (error) {
            console.error(error);
        }
    };

    @action public handleDeleteLiveNotification = async (): Promise<void> => {
        const liveNotificationItem = this.liveNotificationItem();
        if (liveNotificationItem === null) {
            console.error('handleDeleteLiveNotification - ignore');
            return;
        }

        const removeResult = await this.trpcClient.client.liveNotificationsRouter.deleteLiveNotification.mutate({
            id: liveNotificationItem.id,
        });

        if (removeResult.status === 200) {
            await this.liveNotificationsState.liveNotificationsResource.refresh();
        } else {
            console.log('Error deleting carousel');
        }
    };
}
